import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {WebPViaServerDialogComponent} from "../../dialogs/webp-via-server-dialog/webp-via-server-dialog.component";
import {DbModalService} from "../../../../../modules/modal/modal";
import {first, takeUntil} from "rxjs/operators";
import {BoosterService} from "../../services/booster.service";
import {FormControl, FormGroup} from "@angular/forms";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../store';
import {Subject} from "rxjs";
import {UserSubscription} from "../../../../../user/user-subscription";
import {DialogService} from "../../../../../../ng2-bootstrap-modal/dialog.service";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {ActivatedRoute, Router} from "@angular/router";
import {Observable} from "rxjs";
import {ActivityAction, ActivityStatus, IActivity} from "../../../../../activities/activity";
import {IBoosterStats} from "../../../../../store/reducers/booster.reducer";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {BoosterPage} from "../../mappers/booster-page";
import {BoosterPagesCompressed} from "../optimize-images/models/booster-pages-compressed.model";
import {combineLatest} from "rxjs";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {Utils} from "../../../../../shared/utils/utils";

@Component({
    selector: 'db-optimize-images-free',
    templateUrl: './optimize-images-free.component.html',
    styleUrls: ['./optimize-images-free.component.scss'],
    standalone: false
})
export class OptimizeImagesComponentFree implements OnInit, OnDestroy {

  @Input() currentSubscription: UserSubscription;
  public optimizerDisabled: boolean;
  public boosterSettings: any;

  public websiteId: number;
  public websiteUrl: string;
  public websiteHosted: boolean;
  public websiteServerType: string;
  public imageOptimizerSettings: FormGroup = new FormGroup({
    'autoOptimize': new FormControl(null),
    'webpStatus': new FormControl(null),
    'pictureWebpStatus': new FormControl(null)
  });
  public loading: boolean = true;
  public loadingOptimizedPage: boolean = false;
  public stats: IBoosterStats;
  public error: boolean = false;
  public freePlan: boolean = false;
  public webpServerConfigIsActive: boolean = true;
  public pageLayout: boolean = false;
  public loadingWebpFormat: boolean = false;
  public loadingAutoOptimize: boolean = false;
  public ongoingActivity$: Observable<IActivity[]>;
  public optimizedPages: BoosterPage[] | null = null;
  public optimizedImagesSum: number = 0;
  public optimizingPageId: number;
  public showPercentOffer: boolean;
  public pagesCompressed: BoosterPagesCompressed[] | null = null;


  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _dbModal: DbModalService,
    private _boosterService: BoosterService,
    private _store: Store<fromStore.DbStoreState>,
    private _dialogService: DialogService,
    private _route: ActivatedRoute,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) {}

  ngOnInit() {
    this._initData();
    this._showPercentOffer();
    this._addEventHandlers();
    this._getOngoingActivities();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _showPercentOffer() {
    const percentOfferEndDate = new Date("2024-01-30 18:00:00").getTime();
    const datesDiff = Utils.getTimeDiffFromToday(percentOfferEndDate);
    this.showPercentOffer = datesDiff && this.currentSubscription && this.currentSubscription.trialBoosterClient;
  }

  private _getOngoingActivities(): void {
    this.ongoingActivity$ = this._store.select(fromStore.getOngoingActivities);
    this.ongoingActivity$
      .subscribe((data): void => {
        const action = data && data.length && data.find(action => action.action === "custom_page_is_optimizing");
        if (TypeUtils.isDefined(action) && TypeUtils.isDefined(action.action)) {
          this.optimizingPageId = action.meta && action.meta.pageId ? action.meta.pageId : null;
          this.loadingOptimizedPage = action.status !== ActivityStatus.DONE;
          if (!this.loadingOptimizedPage) {
            this._getWebsiteBoosterPagesAndStats();
          }
        }
      });
  }

  private _initData(): void {
    this.pageLayout = this._route.snapshot.data['pageId'] === 'image-optimizer';
    const website = this._route.snapshot.data['pageId'] === 'image-optimizer' ? this._route.snapshot.parent.data['website'] : this._route.snapshot.parent.parent.data['website'];
    this.websiteId = website.id;
    this.websiteUrl = website.site_url;
    this.websiteHosted = !!website.domain_hash;
    this.websiteServerType = website.serverType;
    this.freePlan = this.currentSubscription && (this.currentSubscription.trialBoosterClient || this.currentSubscription.trialClient || this.currentSubscription.bundleClient || this.currentSubscription.oneProductClient);
  }

  private _addEventHandlers(): void {
    this._store.select(fromStore.getOngoingActivities)
      .subscribe((data: any) => {
        const optimizerActivity = data && data.length && data.find(action => [ActivityAction.OPTIMIZE].includes(<any>action.action) );
        if (!TypeUtils.isEmpty(optimizerActivity)) {
          if ( optimizerActivity.status === 'pending' && TypeUtils.isEmpty(optimizerActivity.meta)) {
            this.loading = true;
          }
          if (optimizerActivity.status === 'done' || optimizerActivity.status === 'failed' || optimizerActivity.status === 'canceled') {
            this._getOptimizerStats();
            this._getWebsiteBoosterPagesAndStats();
          }
        }
      });

    if (this.pageLayout) {
      this._getImageOptimizer();
    }
    else {
      this._getWebsiteBoosterPagesAndStats();
    }

    this._getBoosterSettings();
    this._getAutoOptimize();
  }

  private _getImageOptimizer(): void {
    this._store.select(fromStore.getWebsiteImageOptimizerPlugin(this.websiteId))
      .pipe(takeUntil(this._destroyStream$))
      .subscribe((imageOptimizerPlugin): void => {
        if (imageOptimizerPlugin) {
          this.optimizerDisabled = !imageOptimizerPlugin
            || (imageOptimizerPlugin && (!imageOptimizerPlugin.paidVersionInstalled || !imageOptimizerPlugin.active));
          if (!this.optimizerDisabled) {
            this.loading = true;
            this._getOptimizerStats();
          }
        }
      });
  }

  private _getBoosterSettings(): void {
    this._store.select(fromStore.getWebsiteWebpStatus(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(webpStatus => {
        if (!TypeUtils.isNull(webpStatus)) {
          this.loadingWebpFormat = false;
          this.loading = false;
          this.boosterSettings = webpStatus;
          this.imageOptimizerSettings.controls['webpStatus'].setValue(TypeUtils.isDefined(this.boosterSettings.webpStatus) ? this.boosterSettings.webpStatus : false);
          this.imageOptimizerSettings.controls['pictureWebpStatus'].setValue(TypeUtils.isDefined(this.boosterSettings.pictureWebpStatus) ? this.boosterSettings.pictureWebpStatus : false);
          this.webpServerConfigIsActive = TypeUtils.isDefined(this.boosterSettings) && !TypeUtils.isNull(this.boosterSettings) && TypeUtils.isDefined(this.boosterSettings.webpStatus) && !this.boosterSettings.webpStatus
            && TypeUtils.isDefined(this.boosterSettings.webpDeliveryWorking) && !!this.boosterSettings.webpDeliveryWorking;
        }
      });
  }

  private _getAutoOptimize(): void {
    this._store.select(fromStore.getImageOptimizerAutoOptimizeEnabled(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(autoOptimizeEnabled => {
        if (!TypeUtils.isNull(autoOptimizeEnabled) && TypeUtils.isDefined(autoOptimizeEnabled)) {
          this.imageOptimizerSettings.controls['autoOptimize'].setValue(autoOptimizeEnabled);
        }
      });
  }

  private _getWebsiteBoosterPagesAndStats(): void {
    combineLatest(
      this._store.select(fromStore.getWebsiteBoosterPages(this.websiteId)),
      this._store.select(fromStore.getWebsiteBoosterStats(this.websiteId))
    )
      .pipe(
        first(([pages]) => !!pages, null),
      )
      .subscribe(([pages, stats]) => {
        this.optimizedPages = pages;
        if (!TypeUtils.isNull(stats)) {
          this.stats = stats;
          this._getCompressedPages(pages, stats);
          this.error = false;
        }
        else {
          this._getOptimizerStats();
        }
      });
  }

  private _getCompressedPages(pages, stats): void {
    const filteredArray = pages;
    pages.map((optimizedPage: BoosterPage, index): void => {
        stats.pagesCompressed.map((page) => {
          if ((page.page_id).toString() === (optimizedPage.id).toString()) {
            this.optimizedImagesSum = this.optimizedImagesSum + page.images_count;
            filteredArray[index] = (new BoosterPagesCompressed(page, optimizedPage.url));
          }
        });

    });

    this.pagesCompressed = stats && stats.pagesCompressed && stats.pagesCompressed.length ? filteredArray : pages;
  }

  private _getOptimizerStats(): void {
    this._store.select(fromStore.getImageOptimizerStats(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((stats): void => {
        this.loading = false;
        if (!TypeUtils.isNull(stats)) {
          this.stats = stats;
          this._getCompressedPages(this.optimizedPages, this.stats);
          this.loading = this.stats.compressIsRunning || false;
          this._store.dispatch(new fromStore.SetBoosterStats({
            websiteId: this.websiteId,
            stats: stats
          }));
        }
        else {
          this.error = true;
        }
      });
  }

  public setAutoOptimize(): void {
    this.imageOptimizerSettings.controls['autoOptimize'].setValue(false);
    this._router.navigate(['/upgrade-plan']);
    return;
  }

  public getWebpStatus(): void {
    this.loadingWebpFormat = true;
    this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.websiteId}));
    setTimeout( () => {
      NotifierComponent.notify(new Notification('Webp status checked successfully', NotificationType.SUCCESS));
      this.loadingWebpFormat = false;
    }, 3000);
  }

  public setWebpStatus(): void {
    this.loadingWebpFormat = true;
    this._boosterService.setWebpStatus(this.websiteId, this.imageOptimizerSettings.value.webpStatus, this.imageOptimizerSettings.value.pictureWebpStatus)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(() => {
        this.getWebpStatus();
      });
  }

  public showWebPServerDialog(): void {
    const modal = this._dbModal.open(WebPViaServerDialogComponent);
    modal.componentInstance.serverType = this.websiteServerType;
  }

  public sendEventWpSupport(): void {
    window.open('https://wordpress.org/support/plugin/tenweb-speed-optimizer/', '_blank', 'noopener,noreferrer,');
  }

}
